import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AppLayout } from '../../components/layout/AppLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  User, 
  Lock, 
  CreditCard, 
  Settings, 
  HelpCircle,
  FileText,
  LogOut,
  ChevronRight,
  Bell,
  Moon,
  Globe
} from 'lucide-react';

export const AccountPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const sections = [
    {
      title: 'Personal',
      items: [
        { label: 'My Profile', path: '/user/profile', icon: User },
      ]
    },
    {
      title: 'Security',
      items: [
        { label: 'Change Password', path: '/user/profile', icon: Lock },
      ]
    },
    {
      title: 'Preferences',
      items: [
        { label: 'Notifications', path: '/user/notifications', icon: Bell },
        { label: 'Theme', action: 'theme', icon: Moon },
      ]
    },
    {
      title: 'Support',
      items: [
        { label: 'Help Center', path: '/user/support', icon: HelpCircle },
      ]
    },
    {
      title: 'Legal',
      items: [
        { label: 'Terms & Conditions', path: '/terms', icon: FileText },
        { label: 'Privacy Policy', path: '/privacy', icon: FileText },
      ]
    },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <AppLayout>
      <div className="p-4 space-y-6">
        {/* Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {user?.name || 'User'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {user?.email}
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/user/profile')}
              >
                Edit
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <Card key={section.title}>
              <CardContent className="pt-6">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                  {section.title}
                </h3>
                <div className="space-y-2">
                  {section.items.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <button
                        key={item.label}
                        onClick={() => item.path && navigate(item.path)}
                        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <ItemIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.label}
                          </span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Logout Button */}
        <Button
          variant="destructive"
          className="w-full"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
    </AppLayout>
  );
};
